import { Suspense, useEffect, useState } from "react";
import { Outlet, useParams, useNavigate, useLocation } from "react-router";
import { useSetAtom, useAtomValue } from "jotai";
import Sidebar from "./Sidebar";
import WorkerStatus from "./WorkerStatus";
import DevMenu from "./DevMenu";
import SeatGate from "./SeatGate";
import LapsedBanner from "./LapsedBanner";
import { RouteFallback } from "./RouteFallback";
import { seatGatedAtom } from "../stores/seatGate";
import Onboarding from "../features/onboarding/Onboarding";
import ContextFeedbackButton from "../features/feedback/ContextFeedbackButton";
import { useGalaxyInit } from "../lib/hooks/useGalaxyInit";
import { useNavDrawer } from "../lib/hooks/useNavDrawer";
import { useEdgeSwipeDrawer } from "../lib/hooks/useEdgeSwipeDrawer";
import { setSelectedAccountAtom, accountsAtom } from "../stores/galaxy";
import { sharedGalaxiesAtom, sharedGalaxiesLoadedAtom, refreshSharedGalaxiesAtom } from "../stores/shared";
import { authAtom } from "../stores/auth";
import { onboardingApi } from "../api/client";

// Layout owns the URL → selectedAccountAtom write-through. The atom is
// a downstream MIRROR of useParams().account, never a competing source
// of truth. Pages must read galaxy scope from useParams directly; the
// atom is only there for chrome (Sidebar avatar, GalaxySwitcher current
// selection) where the URL isn't ergonomic to thread through.
//
// See feedback_galaxy_url_truth — the bug this PR closes was pages
// reading the atom for fetches and getting wrong-galaxy data when the
// atom lagged the URL on deep-link / post-login redirect / hydration.
export default function Layout() {
  const { open: sidebarOpen, setOpen: setSidebarOpen } = useNavDrawer();
  // Left-edge swipe opens the mobile drawer, swipe-left closes it (mobile only).
  useEdgeSwipeDrawer(sidebarOpen, setSidebarOpen);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(true);
  const { account = "" } = useParams<{ account: string }>();
  const setSelectedAccount = useSetAtom(setSelectedAccountAtom);
  const accounts = useAtomValue(accountsAtom);
  const auth = useAtomValue(authAtom);
  // Seat gate (private-mode galaxy, no seat → 402 needs_seat). Cleared whenever
  // the active galaxy changes so switching to a public/seated one drops the gate
  // before its data loads; re-set by the api layer if the new galaxy 402s too.
  const seatGated = useAtomValue(seatGatedAtom);
  const setSeatGated = useSetAtom(seatGatedAtom);
  useEffect(() => {
    setSeatGated(false);
  }, [account, setSeatGated]);
  const navigate = useNavigate();
  // The seat gate replaces the galaxy view, but must NOT cover the /plan route —
  // that's where the "get a seat" CTA sends the viewer to buy Nova (mirrors the
  // BE seatGateExemptPath for /billing + /plan). seatGated only clears on an
  // account change, so navigating to /plan within the SAME gated galaxy would
  // otherwise keep SeatGate painted over the Plan outlet and the CTA looks dead.
  const { pathname } = useLocation();
  const onGateExemptRoute = pathname.endsWith("/plan");

  useGalaxyInit();

  // URL → atom sync. Validate the URL galaxy is one the user can see;
  // unknown galaxy redirects to the user's primary or first accessible.
  // A galaxy shared read-only with the viewer (docs/design/shared-galaxies.md) is
  // NOT in `accounts` (they aren't a member), so it must be recognized separately
  // or a guest deep-linking to /{shared-login} would be bounced. The list is
  // lazy — if we land on an unknown account and it hasn't loaded yet, fetch it and
  // re-validate rather than redirecting away prematurely.
  const sharedGalaxies = useAtomValue(sharedGalaxiesAtom);
  const sharedLoaded = useAtomValue(sharedGalaxiesLoadedAtom);
  const refreshShared = useSetAtom(refreshSharedGalaxiesAtom);
  useEffect(() => {
    if (!account) return;

    const known = accounts.includes(account) || sharedGalaxies.some((g) => g.account_login === account);
    if (accounts.length > 0 && !known) {
      // Could be a shared galaxy we haven't fetched yet — load once, then let this
      // effect re-run (sharedLoaded/sharedGalaxies change) before deciding to bounce.
      if (!sharedLoaded) {
        void refreshShared();
        return;
      }
      const fallback = auth.user?.login || accounts[0];
      if (fallback && fallback !== account) {
        navigate(`/${fallback}`, { replace: true });
        return;
      }
    }

    setSelectedAccount(account);
  }, [account, accounts, sharedGalaxies, sharedLoaded, refreshShared, setSelectedAccount]);

  // Check onboarding for current galaxy (URL-derived).
  useEffect(() => {
    setOnboardingLoading(true);
    setNeedsOnboarding(false);
    if (!account) return;
    onboardingApi
      .status(account)
      .then((s) => setNeedsOnboarding(!s.onboarding_completed))
      .catch(() => setNeedsOnboarding(false))
      .finally(() => setOnboardingLoading(false));
  }, [account]);

  // The shell uses h-dvh, not h-screen: on iOS Safari 100vh is the LARGER
  // viewport (URL/tab bar expanded), so the shell ran taller than the visible
  // area and the sidebar's bottom (account menu) was pushed under the browser
  // chrome. dvh tracks the actual visible height, so the footer stays on-screen.
  return (
    <div className="relative flex h-dvh" style={{ backgroundColor: "var(--bg-void)" }}>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar. lg:h-full gives the desktop (static) wrapper an EXPLICIT height
          (100% of the h-dvh shell) instead of relying on flex-stretch — iOS
          Safari doesn't always treat a stretched flex item's height as definite,
          which left the Sidebar's inner min-h-full unresolved so its footer
          floated mid-column. On mobile the fixed inset-y-0 already gives a
          definite height. */}
      <div
        className={`fixed inset-y-0 left-0 z-30 w-64 transform transition-transform lg:static lg:h-full lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <Sidebar onClose={() => setSidebarOpen(false)} />
      </div>

      {/* Main content. While the mobile drawer is open we lock the main scroller
          (overflow-hidden) so a swipe over the drawer can't scroll the content
          behind it; lg:overflow-auto keeps the desktop layout (no drawer)
          always scrollable. */}
      <main
        className={`relative z-10 flex-1 ${
          sidebarOpen ? "overflow-hidden lg:overflow-auto" : "overflow-auto"
        }`}
      >
        <WorkerStatus accountLogin={account} />
        {/* Mobile header */}
        <div
          className="flex items-center gap-md border-b p-lg lg:hidden"
          style={{ borderColor: "var(--border)" }}
        >
          <button
            onClick={() => setSidebarOpen(true)}
            className="transition-colors"
            style={{ color: "var(--text-muted)" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = "var(--text-strong)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = "var(--text-muted)";
            }}
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <span
            style={{
              fontFamily: "var(--display)",
              fontSize: "1.0625rem",
              fontWeight: 700,
              letterSpacing: "-0.02em",
              color: "var(--text-strong)",
            }}
          >
            OrbitLens{" "}
            <span style={{ fontWeight: 600, color: "var(--gold)" }}>Ace</span>
          </span>
        </div>

        <div className="p-lg sm:p-2xl lg:p-3xl">
          {/* Lapsed-Nova notice sits above the content on every page (the fallout
              is org-wide + urgent). Renders nothing unless the org's plan has
              lapsed. Not shown during onboarding (a lapsed org isn't onboarding). */}
          {!(( !onboardingLoading && needsOnboarding )) && <LapsedBanner />}
          {!onboardingLoading && needsOnboarding ? (
            <Onboarding onComplete={() => setNeedsOnboarding(false)} />
          ) : seatGated && !onGateExemptRoute ? (
            <SeatGate />
          ) : (
            // Route-level Suspense: the lazy() page chunks suspend here, INSIDE
            // the content slot, so the sidebar/header chrome stays painted while
            // a page chunk downloads (frontend-manifest §3 — localized, never the
            // whole screen). Cached chunks don't re-suspend, so in-page state
            // (pagination, ?team= scope) is untouched on revisit.
            <Suspense fallback={<RouteFallback />}>
              <Outlet />
            </Suspense>
          )}
        </div>
        {/* Context-attached feedback — present on every galaxy page once past
            onboarding. Captures the current view (page/galaxy/member/domain). */}
        {!onboardingLoading && !needsOnboarding && <ContextFeedbackButton />}
      </main>
      {/* Developer tools — renders only when the backend confirms dev tools are
          on (non-production). No-op in prod (the endpoint 404s). */}
      <DevMenu />
    </div>
  );
}
